/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving and saving a pharmacy.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialPharmacyState {
    pharmacy: Dtos.Pharmacy | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
    saveState: IRequestState<Dtos.ResponseStatus>;
    quarantineStockState: IRequestState<Dtos.ResponseStatus>;
    actionQuarantinedStockState: IRequestState<Dtos.ResponseStatus>;
};

export interface IPharmacyState {
    pharmacy: IInitialPharmacyState;
};

export interface IQuarantinePharmacyStockForm {
    id: number;
};

export interface IActionQuarantinedStockForm {
    id: number;
    quarantinedStockAction: Dtos.QuarantinedStockAction;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const PharmacyInitialState: IInitialPharmacyState = {
    pharmacy: null,
    loadState: {
        state: RequestState.None
    },
    saveState: {
        state: RequestState.None
    },
    quarantineStockState: {
        state: RequestState.None
    },
    actionQuarantinedStockState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const usePharmacySelector: TypedUseSelectorHook<IPharmacyState> = useSelector;

export const PharmacySelectors = {
    selectPharmacy: (state: IPharmacyState) => state.pharmacy.pharmacy,
    selectLoadState: (state: IPharmacyState) => state.pharmacy.loadState,
    selectSaveState: (state: IPharmacyState) => state.pharmacy.saveState,
    selectQuarantineStockState: (state: IPharmacyState) => state.pharmacy.quarantineStockState,
    selectActionQuarantinedStockState: (state: IPharmacyState) => state.pharmacy.actionQuarantinedStockState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class PharmacyReducer extends ImmerReducer<IInitialPharmacyState> {
    public loadById(id: number) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadByInstCode(institutionCode: string) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(pharmacy?: Dtos.Pharmacy) {
        this.draftState.pharmacy = pharmacy ? pharmacy : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.pharmacy = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public save(form: Dtos.Pharmacy) {
        this.draftState.saveState = {
            state: RequestState.Pending
        };
    }

    public saveSuccess(pharmacy?: Dtos.Pharmacy) {
        this.draftState.pharmacy = pharmacy ? pharmacy : null;
        this.draftState.saveState = {
            state: RequestState.Success
        };
    }

    public saveFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.pharmacy = null;
        this.draftState.saveState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public quarantineStock(form: IQuarantinePharmacyStockForm) {
        this.draftState.quarantineStockState = {
            state: RequestState.Pending
        };
    }

    public quarantineStockSuccess() {
        this.draftState.quarantineStockState = {
            state: RequestState.Success
        };
    }

    public quarantineStockFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.quarantineStockState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public actionQuarantinedStock(form: IActionQuarantinedStockForm) {
        this.draftState.actionQuarantinedStockState = {
            state: RequestState.Pending
        };
    }

    public actionQuarantinedStockSuccess() {
        this.draftState.actionQuarantinedStockState = {
            state: RequestState.Success
        };
    }

    public actionQuarantinedStockFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.actionQuarantinedStockState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...PharmacyInitialState };
    }
};

export const PharmacyActions = createActionCreators(PharmacyReducer);
const reducer = createReducerFunction(PharmacyReducer, PharmacyInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const PharmacyApi = {
    loadById: (id: number) => {
        return client.get(new Dtos.GetPharmacyById({
            id
        }));
    },
    loadByInstCode: (institutionCode: string) => {
        return client.get(new Dtos.GetPharmacyByInstitutionCode({
            institutionCode
        }));
    },
    save: (pharmacy?: Dtos.Pharmacy) => {
        return client.post(new Dtos.SetPharmacy({
            pharmacy
        }));
    },
    quarantineStock: (form: IQuarantinePharmacyStockForm) => {
        return client.post(new Dtos.QuarantinePharmacyStock({
            id: form.id
        }));
    },
    actionQuarantinedStock: (form: IActionQuarantinedStockForm) => {
        return client.post(new Dtos.ActionQuarantinedStock({
            id: form.id,
            quarantinedStockAction: form.quarantinedStockAction
        }));
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const PharmacyLogic = {
    loadById: createLogic<IPharmacyState, {}, undefined, string, ReturnType<typeof PharmacyActions.loadById>>({
        type: PharmacyActions.loadById.type,
        process: async ({ action }, dispatch, done) => {
            const id = action.payload;

            try {
                const response = await PharmacyApi.loadById(id);

                dispatch(PharmacyActions.loadSuccess(
                    response.pharmacy
                ));
            }
            catch (error: any) {
                dispatch(PharmacyActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    loadByInstCode: createLogic<IPharmacyState, {}, undefined, string, ReturnType<typeof PharmacyActions.loadByInstCode>>({
        type: PharmacyActions.loadByInstCode.type,
        process: async ({ action }, dispatch, done) => {
            const institutionCode = action.payload;

            try {
                const response = await PharmacyApi.loadByInstCode(institutionCode);

                dispatch(PharmacyActions.loadSuccess(
                    response.pharmacy
                ));
            }
            catch (error: any) {
                dispatch(PharmacyActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    save: createLogic<IPharmacyState, {}, undefined, string, ReturnType<typeof PharmacyActions.save>>({
        type: PharmacyActions.save.type,
        process: async ({ action }, dispatch, done) => {
            const pharmacy = action.payload;

            try {
                const response = await PharmacyApi.save(pharmacy);

                dispatch(PharmacyActions.saveSuccess(
                    response.pharmacy
                ));
            }
            catch (error: any) {
                dispatch(PharmacyActions.saveFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    quarantineStock: createLogic<IPharmacyState, {}, undefined, string, ReturnType<typeof PharmacyActions.quarantineStock>>({
        type: PharmacyActions.quarantineStock.type,
        process: async ({ action }, dispatch, done) => {
            const formData = action.payload;

            try {
                await PharmacyApi.quarantineStock(formData);

                dispatch(PharmacyActions.quarantineStockSuccess(
                ));
            }
            catch (error: any) {
                dispatch(PharmacyActions.quarantineStockFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    }),
    actionQuarantinedStock: createLogic<IPharmacyState, {}, undefined, string, ReturnType<typeof PharmacyActions.actionQuarantinedStock>>({
        type: PharmacyActions.actionQuarantinedStock.type,
        process: async ({ action }, dispatch, done) => {
            const formData = action.payload;

            try {
                await PharmacyApi.actionQuarantinedStock(formData);

                dispatch(PharmacyActions.actionQuarantinedStockSuccess(
                ));
            }
            catch (error: any) {
                dispatch(PharmacyActions.actionQuarantinedStockFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    PharmacyLogic.loadById,
    PharmacyLogic.loadByInstCode,
    PharmacyLogic.save,
    PharmacyLogic.quarantineStock,
    PharmacyLogic.actionQuarantinedStock
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const usePharmacy = (): [Dtos.Pharmacy | null, IRequestState<Dtos.ResponseStatus>, IRequestState<Dtos.ResponseStatus>, typeof PharmacyActions] => {
    const pharmacy = useSelector(PharmacySelectors.selectPharmacy);
    const loadState = useSelector(PharmacySelectors.selectLoadState);
    const saveState = useSelector(PharmacySelectors.selectSaveState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PharmacyActions>(() => {
        return bindActionCreators(PharmacyActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PharmacyActions, dispatch));
    }, [dispatch, setActions]);

    return [
        pharmacy,
        loadState,
        saveState,
        actions
    ];
};

export const useQuarantinePharmacyStock = (): [IRequestState<Dtos.ResponseStatus>, typeof PharmacyActions] => {
    const quarantineStockState = useSelector(PharmacySelectors.selectQuarantineStockState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PharmacyActions>(() => {
        return bindActionCreators(PharmacyActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PharmacyActions, dispatch));
    }, [dispatch, setActions]);

    return [
        quarantineStockState,
        actions
    ];
};

export const useActionQuarantinedPharmacyStock = (): [IRequestState<Dtos.ResponseStatus>, typeof PharmacyActions] => {
    const actionQuarantinedStockState = useSelector(PharmacySelectors.selectActionQuarantinedStockState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof PharmacyActions>(() => {
        return bindActionCreators(PharmacyActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(PharmacyActions, dispatch));
    }, [dispatch, setActions]);

    return [
        actionQuarantinedStockState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('pharmacy', reducer as any, logics as any);